import { useState, useEffect } from 'react'
import { InfoTip } from '../../components/InfoTip'
import { useExecutions, categorizeExecutions } from '../../context/ExecutionContext'
import { formatLotsQtyCompact } from '../../utils/lotSize'
import type { StrategyExecution } from '../../types'

export function TraderArchived() {
  const { executions: execs, refreshExecutions } = useExecutions()
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({})
  const [filterStrategy, setFilterStrategy] = useState<'all' | 'rsi_straddle' | 'trending_expiry'>('all')
  const [filterUnderlying, setFilterUnderlying] = useState<string>('all')
  const [sortBy, setSortBy] = useState<'date' | 'pnl'>('date')
  const [fromDate, setFromDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 90); return d.toISOString().split('T')[0] })
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0])

  useEffect(() => {
    refreshExecutions()
  }, [refreshExecutions])

  const { archived } = categorizeExecutions(execs)

  // Apply filters
  let filtered = archived
  if (filterStrategy !== 'all') {
    filtered = filtered.filter((e) => e.strategyType === filterStrategy)
  }
  if (filterUnderlying !== 'all') {
    filtered = filtered.filter((e) => e.underlying === filterUnderlying)
  }
  // Date range filter
  filtered = filtered.filter((e) => {
    const d = e.createdAt.split('T')[0]
    if (fromDate && d < fromDate) return false
    if (toDate && d > toDate) return false
    return true
  })

  // Sort
  if (sortBy === 'pnl') {
    filtered = [...filtered].sort((a, b) => b.state.mtm.totalPnl - a.state.mtm.totalPnl)
  } else {
    filtered = [...filtered].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }

  // Group by date for display
  const grouped = filtered.reduce<Record<string, StrategyExecution[]>>((acc, exec) => {
    const date = exec.createdAt.split('T')[0]
    if (!acc[date]) acc[date] = []
    acc[date].push(exec)
    return acc
  }, {})

  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a))

  // Unique underlyings for filter
  const underlyings = Array.from(new Set(archived.map((e) => e.underlying)))

  // Summary stats
  const totalPnl = filtered.reduce((s, e) => s + e.state.mtm.totalPnl, 0)
  const totalTrades = filtered.length
  const wins = filtered.filter((e) => e.state.mtm.totalPnl > 0).length
  const losses = filtered.filter((e) => e.state.mtm.totalPnl < 0).length
  const winRate = totalTrades > 0 ? Math.round((wins / totalTrades) * 100) : 0

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00')
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
  }

  return (
    <div>
      <div className="page-head">
        <h1>📁 Archived Strategies</h1>
        <p className="page-sub">Completed strategies from the last 90 days</p>
      </div>

      {/* ─── Summary KPIs ─── */}
      <div className="kpi-strip">
        <div className="kpi-card">
          <div className="kpi-label">Total P&L <InfoTip term="totalPnl" /></div>
          <div className={`kpi-value ${totalPnl >= 0 ? 'kpi-positive' : 'kpi-negative'}`}>
            {totalPnl >= 0 ? '+' : ''}₹{totalPnl.toLocaleString()}
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Total Sets</div>
          <div className="kpi-value">{totalTrades}</div>
          <div className="kpi-sub">execution sets</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Win Rate</div>
          <div className={`kpi-value ${winRate >= 50 ? 'kpi-positive' : 'kpi-negative'}`}>{winRate}%</div>
          <div className="kpi-sub">{wins}W / {losses}L</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Avg P&L / Set</div>
          <div className={`kpi-value ${totalPnl >= 0 ? 'kpi-positive' : 'kpi-negative'}`}>
            ₹{totalTrades > 0 ? Math.round(totalPnl / totalTrades).toLocaleString() : '0'}
          </div>
        </div>
      </div>

      {/* ─── Filters ─── */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
          <span className="card-title">Filters</span>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginLeft: 'auto' }}>
            <select className="cfg-input" style={{ width: 'auto', minWidth: 120 }}
              value={filterStrategy} onChange={(e) => setFilterStrategy(e.target.value as any)}>
              <option value="all">All Strategies</option>
              <option value="rsi_straddle">RSI Straddle</option>
              <option value="trending_expiry">Trending Expiry</option>
            </select>
            <select className="cfg-input" style={{ width: 'auto', minWidth: 120 }}
              value={filterUnderlying} onChange={(e) => setFilterUnderlying(e.target.value)}>
              <option value="all">All Underlyings</option>
              {underlyings.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
            <select className="cfg-input" style={{ width: 'auto', minWidth: 120 }}
              value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}>
              <option value="date">Sort by Date</option>
              <option value="pnl">Sort by P&L</option>
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>From</label>
              <input type="date" className="cfg-input" style={{ width: 'auto' }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>To</label>
              <input type="date" className="cfg-input" style={{ width: 'auto' }} value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {/* ─── Grouped by Date ─── */}
      {sortedDates.length === 0 ? (
        <div className="card">
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <p style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>No archived strategies</p>
            <p style={{ fontSize: '0.8125rem' }}>Completed strategies from the last 90 days will appear here</p>
          </div>
        </div>
      ) : (
        sortedDates.map((date) => {
          const dayExecs = grouped[date]
          const dayPnl = dayExecs.reduce((s, e) => s + e.state.mtm.totalPnl, 0)

          return (
            <div className="card" key={date} style={{ marginBottom: '1rem' }}>
              <div className="card-header">
                <span className="card-title">{formatDate(date)}</span>
                <span style={{ marginLeft: 'auto', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <span className="kpi-sub">{dayExecs.length} set{dayExecs.length !== 1 ? 's' : ''}</span>
                  <span className={dayPnl >= 0 ? 'kpi-positive' : 'kpi-negative'} style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: '0.875rem' }}>
                    {dayPnl >= 0 ? '+' : ''}₹{dayPnl.toLocaleString()}
                  </span>
                </span>
              </div>
              <div className="exec-card-list">
                {dayExecs.map((exec) => {
                  const isExpanded = expandedCards[exec.id]
                  const statusColor = exec.status === 'running' ? 'var(--green)' : exec.status === 'paused' ? 'var(--yellow)' : 'var(--red)'

                  return (
                    <div className="exec-card" key={exec.id} style={{ borderLeftColor: statusColor, cursor: 'pointer' }}
                      onClick={() => setExpandedCards((prev) => ({ ...prev, [exec.id]: !prev[exec.id] }))}>
                      <div className="exec-card-top">
                        <div className="exec-card-identity">
                          <span className="exec-card-name">{exec.setName}</span>
                          <span className="exec-card-strat">{exec.strategyType === 'rsi_straddle' ? 'RSI Straddle' : 'Trending'}</span>
                          <span className="exec-card-ul">{exec.underlying} <span className="exec-card-expiry">{(exec.params as any)?.expiryType || 'weekly'}</span></span>
                          {(exec.params as any)?.quantityLots && (
                            <span className="exec-card-lots" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{formatLotsQtyCompact((exec.params as any).quantityLots, exec.underlying)}</span>
                          )}
                        </div>

                        <div className="exec-card-metrics">
                          <div className="exec-card-chip">
                            <span className="exec-chip-label">P&L</span>
                            <span className={`exec-chip-val ${(exec.state.mtm?.totalPnl || 0) >= 0 ? 'green' : 'red'}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                              {(exec.state.mtm?.totalPnl || 0) >= 0 ? '+' : ''}₹{(exec.state.mtm?.totalPnl || 0).toLocaleString()}
                            </span>
                          </div>
                          <div className="exec-card-chip">
                            <span className="exec-chip-label">Realized</span>
                            <span className={`exec-chip-val ${(exec.state.mtm?.realizedPnl || 0) >= 0 ? 'green' : 'red'}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                              ₹{(exec.state.mtm?.realizedPnl || 0).toLocaleString()}
                            </span>
                          </div>
                        </div>

                        <div className="exec-card-right">
                          <span className={`badge ${exec.status === 'running' ? 'badge-target' : exec.status === 'paused' ? 'badge-entry' : 'badge-sl'}`}>
                            {exec.status}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                            {isExpanded ? '▲' : '▼'}
                          </span>
                        </div>
                      </div>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="exec-card-sub" style={{ flexDirection: 'column', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.8125rem' }}>
                            <span><span style={{ color: 'var(--text-muted)' }}>Broker:</span> {exec.brokerId}</span>
                            <span><span style={{ color: 'var(--text-muted)' }}>Mode:</span> {exec.mode === 'single_client' ? 'Single' : 'Multi'}</span>
                            <span><span style={{ color: 'var(--text-muted)' }}>Re-entries:</span> {exec.state.reentryCount}</span>
                            <span><span style={{ color: 'var(--text-muted)' }}>Created:</span> {new Date(exec.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}
